import React, { useState, useEffect, useCallback } from 'react';

const WEB_SPACE_PRICE_PER_GB = 0.30;
const EMAIL_SPACE_PRICE_PER_GB = 0.20;
const PRICE_PER_CUSTOMER = 1.50;

// Hilfsfunktion für eindeutige IDs
let domainRowIdCounter = 0;
const generateId = () => {
  domainRowIdCounter += 1;
  return Date.now() + domainRowIdCounter; // Kombiniere Zeitstempel und Zähler
}

function PriceCalculator({ tlds = [] }) {
  // State für die ausgewählten Domains (Array von Objekten)
  const [selectedDomains, setSelectedDomains] = useState([
    // Start mit einer leeren/default Zeile
    { id: generateId(), tldName: tlds.length > 0 ? tlds[0].name : '', quantity: 1 }
  ]);
  const [webspaceGb, setWebspaceGb] = useState(10);
  const [emailGb, setEmailGb] = useState(5);
  const [customerCount, setCustomerCount] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);

  // ---- Handler für Domain-Zeilen ----

  // Hinzufügen einer neuen Domain-Zeile
  const addDomainRow = () => {
    // Finde die nächste verfügbare TLD, die noch nicht ausgewählt ist (optional, sonst erste TLD)
    const currentSelectedNames = selectedDomains.map(d => d.tldName);
    const nextAvailableTld = tlds.find(tld => !currentSelectedNames.includes(tld.name));
    const defaultTldName = nextAvailableTld ? nextAvailableTld.name : (tlds.length > 0 ? tlds[0].name : '');

    setSelectedDomains(prevDomains => [
      ...prevDomains,
      { id: generateId(), tldName: defaultTldName, quantity: 1 }
    ]);
  };

  // Entfernen einer Domain-Zeile anhand ihrer ID
  const removeDomainRow = (idToRemove) => {
    // Verhindere das Entfernen der letzten Zeile
    if (selectedDomains.length <= 1) return;
    setSelectedDomains(prevDomains => prevDomains.filter(domain => domain.id !== idToRemove));
  };

  // Aktualisieren einer Domain-Zeile (TLD oder Anzahl)
  const updateDomainRow = (idToUpdate, field, value) => {
    setSelectedDomains(prevDomains =>
      prevDomains.map(domain => {
        if (domain.id === idToUpdate) {
          // Stelle sicher, dass die Menge nicht negativ wird
          const newQuantity = field === 'quantity' ? Math.max(1, parseInt(value) || 1) : domain.quantity;
          const newTldName = field === 'tldName' ? value : domain.tldName;
          return { ...domain, tldName: newTldName, quantity: newQuantity };
        }
        return domain;
      })
    );
  };


  // ---- Preisberechnung ----
  const calculatePrice = useCallback(() => {
    let domainsTotalCost = 0;
    selectedDomains.forEach(selectedDomain => {
      const tldInfo = tlds.find(tld => tld.name === selectedDomain.tldName);
      const tldPrice = tldInfo ? tldInfo.price : 0;
      domainsTotalCost += tldPrice * selectedDomain.quantity;
    });

    const webspaceCost = webspaceGb * WEB_SPACE_PRICE_PER_GB;
    const emailCost = emailGb * EMAIL_SPACE_PRICE_PER_GB;
    const customerCost = customerCount * PRICE_PER_CUSTOMER;

    const total = domainsTotalCost + webspaceCost + emailCost + customerCost;
    setTotalPrice(total);
  }, [selectedDomains, webspaceGb, emailGb, customerCount, tlds]); // Abhängigkeiten

  // Effekt, um den Preis neu zu berechnen, wenn sich Eingaben ändern
  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]); // Nur calculatePrice als Abhängigkeit


  // ---- Handler für andere Inputs ----
  const handleWebspaceChange = (event) => {
    setWebspaceGb(Math.max(0, parseInt(event.target.value) || 0));
  };

  const handleEmailChange = (event) => {
    setEmailGb(Math.max(0, parseInt(event.target.value) || 0));
  };

  const handleCustomerChange = (event) => {
    setCustomerCount(Math.max(0, parseInt(event.target.value) || 0));
  };


  // ---- Rendering ----
  return (
    <div className="price-calculator">
      <h2>Kalkuliere Deine Kosten</h2>
      <p>Schätze Deine monatlichen Kosten für Deine Domains, Kunden, Webspace und E-Mail-Speicher. Es wird nur der <i>tatsächlich belegte</i> Speicherplatz abgerechnet.</p>

      <div className="calculator-form">
        {/* Domain Auswahl (mehrere Zeilen) */}
        <div className="form-section">
           <label className="section-label">Deine Domains <sup className="text-muted">1</sup></label>
           {selectedDomains.map((domain, index) => (
             <div key={domain.id} className="domain-row input-group multi-input-group">
               {/* Anzahl Input */}
               <input
                 type="number"
                 aria-label={`Anzahl für Domain ${index + 1}`}
                 value={domain.quantity}
                 onChange={(e) => updateDomainRow(domain.id, 'quantity', e.target.value)}
                 min="1"
                 className="quantity-input"
               />
                <span className="multiplier">x</span>
               {/* TLD Select */}
               <select
                 aria-label={`TLD Auswahl für Domain ${index + 1}`}
                 value={domain.tldName}
                 onChange={(e) => updateDomainRow(domain.id, 'tldName', e.target.value)}
                 className="tld-select"
                 disabled={tlds.length === 0} // Deaktivieren, wenn keine TLDs übergeben wurden
               >
                {tlds.length === 0 && <option>Keine TLDs verfügbar</option>}
                 {tlds.map((tld) => (
                   <option key={tld.name} value={tld.name}>
                     .{tld.name} (€{tld.price.toFixed(2)}/Monat)
                   </option>
                 ))}
               </select>

               <button
                 type="button"
                 onClick={() => removeDomainRow(domain.id)}
                 className="remove-row-btn"
                 aria-label={`Domainzeile ${index + 1} entfernen`}
                 title="Diese Domainzeile entfernen"
                 disabled={selectedDomains.length <= 1} // Letzte Zeile nicht löschbar
               >
                 &times; {/* Kreuz-Symbol */}
               </button>
               {/* Entfernen Button */}
             </div>
           ))}
           {/* Hinzufügen Button */}
           <button type="button" onClick={addDomainRow} className="add-row-btn">
             + Weitere Domain hinzufügen
           </button>
        </div>


        {/* Kundenanzahl */}
        <div className="form-group">
          <label htmlFor="customer-input">Anzahl Kunden <sup className="text-muted">2</sup></label>
          <div className="input-group">
            <input
              type="number"
              id="customer-input"
              value={customerCount}
              onChange={handleCustomerChange}
              min="0"
              placeholder="z.B. 1"
            />
             <span>Kunden</span>
           </div>
           <small className="text-muted">Preis: €{PRICE_PER_CUSTOMER.toFixed(2).replace('.', ',')} / Kunde / Monat</small>
        </div>

        {/* Webspace */}
        <div className="form-group">
          <label htmlFor="webspace-input">Speicherplatz (Webspace + Datenbanken, in GB)</label>
           <div className="input-group">
             <input
               type="number"
               id="webspace-input"
               value={webspaceGb}
               onChange={handleWebspaceChange}
               min="0"
               placeholder="z.B. 10"
             />
             <span>GB</span>
          </div>
          <small className="text-muted">Preis: €{WEB_SPACE_PRICE_PER_GB.toFixed(2).replace('.', ',')} / GB / Monat</small>
        </div>

        {/* E-Mail Speicher */}
        <div className="form-group">
          <label htmlFor="email-input">E-Mail-Speicher (GB)</label>
           <div className="input-group">
            <input
              type="number"
              id="email-input"
              value={emailGb}
              onChange={handleEmailChange}
              min="0"
              placeholder="z.B. 5"
            />
            <span>GB</span>
           </div>
           <small className="text-muted">Preis: €{EMAIL_SPACE_PRICE_PER_GB.toFixed(2)} / GB / Monat</small>
        </div>

        {/* Preis Anzeige */}
        <div className="price-display">
          <h3>Geschätzte monatliche Kosten:<sup>3, 4</sup></h3>
          <span className="total-price">{totalPrice.toFixed(2).replace('.', ',')} €</span>
        </div>

        <div className="price-calculator-footer text-muted">
          <small>1) Domains werden jeweils für 12 Monate im Voraus abgerechnet, der Preis ist anteilig berechnet.</small>
          <br/>
          <small>2) Der Preis je Kunde ist eine Schätzung, da die Leistung je Kunde im Reseller-Dashboard individuell eingestellt werden kann und dadurch variiert.</small>
          <br/>
          <small>3) Bei dem Preis handelt es sich um eine Schätzung und kein konkretes Angebot. Preis exkl. USt.</small>
          <br/>
          <small>4) Monatlicher Mindestumsatz: 10 €. Bei Nichterreichen dieses Mindestumsatzes wird der Rechnungsbetrag auf 10 € netto aufgerundet.</small>
        </div>
      </div>
    </div>
  );
}

export default PriceCalculator;
